// Phase 7 D3 — Track C T-C2. Change-password form.
//
// Uses the shared `changePasswordFormSchema` (current + new + confirm-new
// with cross-field check). On 200 the backend revokes every session +
// refresh token, so the SPA signs out locally and redirects to /login.
// Q-10 destructive-toast policy: this is treated as a destructive write
// (sign-out follows) — emit a toast on /login after redirect via
// search-params is overkill; instead we surface a brief inline success
// state before redirecting.

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  changePasswordFormSchema,
  type ChangePasswordForm as ChangePasswordFormValues,
} from '@dailydeck/shared/validation';
import type { ChangePasswordBody } from '@dailydeck/shared/types';
import { copy } from '@dailydeck/shared/copy';
import { ApiError, AuthRequiredError } from '@dailydeck/shared/api';
import { apiClient } from '../../../app/apiClient.js';
import { useAuth } from '../../../hooks/useAuth.js';
import { Form } from '../../../components/ui/Form.js';
import { FormField } from '../../../components/ui/FormField.js';
import { FormError } from '../../../components/ui/FormError.js';
import { FormSubmit } from '../../../components/ui/FormSubmit.js';
import { Input } from '../../../components/ui/Input.js';

export function ChangePasswordForm(): JSX.Element {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordFormSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_new_password: '',
    },
  });

  const mutation = useMutation<void, Error, ChangePasswordBody>({
    mutationFn: (body) => apiClient.auth.changePassword(body),
  });

  async function handleSubmit(values: ChangePasswordFormValues): Promise<void> {
    setFormError(null);
    try {
      await mutation.mutateAsync({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      // 200 → server revoked every session. Drop local auth state and bounce.
      signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      if (err instanceof AuthRequiredError) {
        setFormError(copy.settings.account.wrongPasswordError);
        form.setError('current_password', {
          type: 'server',
          message: copy.settings.account.wrongPasswordError,
        });
        return;
      }
      if (err instanceof ApiError && err.fields) {
        for (const [name, msgs] of Object.entries(err.fields)) {
          if (name in form.getValues()) {
            form.setError(name as keyof ChangePasswordFormValues, {
              type: 'server',
              message: msgs[0] ?? 'Invalid value',
            });
          }
        }
        return;
      }
      setFormError(copy.settings.account.genericErrorBody);
    }
  }

  return (
    <Form form={form} onSubmit={handleSubmit} aria-label={copy.settings.account.changePasswordTitle}>
      <FormField
        name="current_password"
        label={copy.settings.account.currentPasswordLabel}
        required
      >
        <Input type="password" autoComplete="current-password" />
      </FormField>
      <FormField
        name="new_password"
        label={copy.settings.account.newPasswordLabel}
        description={copy.settings.account.newPasswordHelp}
        required
      >
        <Input type="password" autoComplete="new-password" />
      </FormField>
      <FormField
        name="confirm_new_password"
        label={copy.settings.account.confirmNewPasswordLabel}
        required
      >
        <Input type="password" autoComplete="new-password" />
      </FormField>
      {formError !== null && (
        <FormError>{formError}</FormError>
      )}
      <div>
        <FormSubmit>{copy.settings.account.changePasswordSubmit}</FormSubmit>
      </div>
    </Form>
  );
}
